import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Loader2, LogOut, Phone, Mail, ArrowLeft, Activity, Users, CreditCard, MousePointerClick } from "lucide-react";
import { useAdminAuth, adminAxios } from "@/lib/admin-auth";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("hu-HU", {
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const { token, email, loading, logout } = useAdminAuth();
  const [tab, setTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [events, setEvents] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    const api = adminAxios(token);
    Promise.all([
      api.get("/admin/analytics"),
      api.get("/admin/contacts"),
      api.get("/admin/transactions"),
      api.get("/admin/events?limit=200"),
    ])
      .then(([a, c, t, e]) => {
        setAnalytics(a.data);
        setContacts(c.data);
        setTransactions(t.data);
        setEvents(e.data);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setDataLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
      </div>
    );
  }
  if (!token) return <Navigate to="/admin/login" replace />;

  const stats = analytics || {};
  const eventsByName = Object.fromEntries((stats.by_event || []).map((e) => [e.event, e.count]));

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0]">
      <header className="border-b border-[#2A2A2A] bg-[#0A0A0A] sticky top-0 z-30 backdrop-blur-lg">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" data-testid="admin-back-site" className="text-sm text-[#A3A3A3] hover:text-[#F5F5F0] inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Vissza az oldalra
            </Link>
            <span className="hidden md:inline text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">Admin · Videosbolt</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-sm text-[#A3A3A3]">{email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-sm text-[#F5F5F0] hover:text-[#D4AF37] transition border border-[#2A2A2A] hover:border-[#D4AF37] px-3 py-1.5 rounded-sm"
            >
              <LogOut className="w-4 h-4" /> Kijelentkezés
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight">Vezérlőpult</h1>
        <p className="mt-2 text-[#A3A3A3]">Beérkezett érdeklődések, fizetések és kattintási statisztikák.</p>

        {dataLoading ? (
          <div className="mt-16 text-center"><Loader2 className="w-6 h-6 mx-auto text-[#D4AF37] animate-spin" /></div>
        ) : (
          <>
            {/* Stat tiles */}
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatTile testId="stat-call-clicks" icon={<Phone />} label="Hívás kattintás (7 nap)" value={stats.call_clicks_last_7_days ?? 0} />
              <StatTile testId="stat-total-contacts" icon={<Users />} label="Érdeklődések" value={stats.total_contacts ?? 0} />
              <StatTile testId="stat-paid" icon={<CreditCard />} label="Fizetett megrendelés" value={stats.paid_transactions ?? 0} />
              <StatTile testId="stat-pending" icon={<Activity />} label="Folyamatban lévő" value={stats.pending_transactions ?? 0} />
            </div>

            {/* Tabs */}
            <div className="mt-12 border-b border-[#2A2A2A] flex gap-1 overflow-x-auto">
              {[
                ["overview", "Áttekintés"],
                ["contacts", `Érdeklődések (${contacts.length})`],
                ["transactions", `Fizetések (${transactions.length})`],
                ["events", "Események"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  data-testid={`admin-tab-${key}`}
                  onClick={() => setTab(key)}
                  className={`px-4 py-3 text-sm transition border-b-2 -mb-px ${
                    tab === key
                      ? "border-[#D4AF37] text-[#F5F5F0]"
                      : "border-transparent text-[#A3A3A3] hover:text-[#F5F5F0]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === "overview" && (
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                <Panel title="Esemény megoszlás">
                  {(stats.by_event || []).length === 0 ? (
                    <p className="text-[#A3A3A3] text-sm">Még nincs adat.</p>
                  ) : (
                    <ul className="divide-y divide-[#2A2A2A]">
                      {stats.by_event.map((e) => (
                        <li key={e.event} className="py-3 flex items-center justify-between">
                          <span className="text-sm text-[#F5F5F0]">{e.event}</span>
                          <span className="font-serif-display text-2xl text-[#D4AF37]">{e.count}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Panel>
                <Panel title="Konverziós ráta">
                  <div className="space-y-4">
                    <Ratio label="Hívás → Érdeklődés" a={eventsByName.call_click || 0} b={stats.total_contacts || 0} />
                    <Ratio label="Érdeklődés → Fizetés" a={stats.total_contacts || 0} b={stats.paid_transactions || 0} />
                  </div>
                </Panel>
              </div>
            )}

            {tab === "contacts" && <ContactsTable contacts={contacts} />}
            {tab === "transactions" && <TransactionsTable transactions={transactions} />}
            {tab === "events" && <EventsTable events={events} />}
          </>
        )}
      </div>
    </div>
  );
}

function StatTile({ icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="bg-[#111111] border border-[#2A2A2A] rounded-sm p-5">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3]">
        <span className="text-[#D4AF37] [&>svg]:w-3.5 [&>svg]:h-3.5">{icon}</span>
        {label}
      </div>
      <div className="mt-3 font-serif-display text-4xl text-[#F5F5F0]">{value}</div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bg-[#111111] border border-[#2A2A2A] rounded-sm p-6">
      <h3 className="font-serif-display text-xl mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Ratio({ label, a, b }) {
  const pct = a > 0 ? Math.min(100, Math.round((b / a) * 100)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-[#A3A3A3]">{label}</span>
        <span className="text-[#F5F5F0]">{b} / {a} · {pct}%</span>
      </div>
      <div className="mt-2 h-1.5 bg-[#0A0A0A] rounded-sm overflow-hidden">
        <div className="h-full bg-[#D4AF37]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ContactsTable({ contacts }) {
  if (contacts.length === 0) return <p className="mt-8 text-[#A3A3A3]">Még nincs érdeklődés.</p>;
  return (
    <div className="mt-8 bg-[#111111] border border-[#2A2A2A] rounded-sm overflow-hidden">
      <table data-testid="admin-contacts-table" className="w-full text-sm">
        <thead className="bg-[#0A0A0A]">
          <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3]">
            <th className="px-4 py-3">Időpont</th>
            <th className="px-4 py-3">Név</th>
            <th className="px-4 py-3">E-mail</th>
            <th className="px-4 py-3">Telefon</th>
            <th className="px-4 py-3">Tételek</th>
            <th className="px-4 py-3">Üzenet</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#2A2A2A]">
          {contacts.map((c) => (
            <tr key={c.id} data-testid={`contact-row-${c.id}`} className="hover:bg-[#0A0A0A]/50">
              <td className="px-4 py-3 text-[#A3A3A3] whitespace-nowrap">{formatDate(c.timestamp)}</td>
              <td className="px-4 py-3 text-[#F5F5F0]">{c.name}</td>
              <td className="px-4 py-3"><a href={`mailto:${c.email}`} className="text-[#D4AF37] hover:underline">{c.email}</a></td>
              <td className="px-4 py-3"><a href={`tel:${c.phone}`} className="text-[#F5F5F0]">{c.phone || "—"}</a></td>
              <td className="px-4 py-3 text-[#A3A3A3]">{c.items || "—"}</td>
              <td className="px-4 py-3 text-[#A3A3A3] max-w-xs truncate" title={c.message}>{c.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TransactionsTable({ transactions }) {
  if (transactions.length === 0) return <p className="mt-8 text-[#A3A3A3]">Még nincs tranzakció.</p>;
  return (
    <div className="mt-8 bg-[#111111] border border-[#2A2A2A] rounded-sm overflow-hidden">
      <table data-testid="admin-transactions-table" className="w-full text-sm">
        <thead className="bg-[#0A0A0A]">
          <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3]">
            <th className="px-4 py-3">Időpont</th>
            <th className="px-4 py-3">Csomag</th>
            <th className="px-4 py-3">Összeg</th>
            <th className="px-4 py-3">Állapot</th>
            <th className="px-4 py-3">Session</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#2A2A2A]">
          {transactions.map((t) => (
            <tr key={t.session_id} data-testid={`transaction-row-${t.session_id}`}>
              <td className="px-4 py-3 text-[#A3A3A3] whitespace-nowrap">{formatDate(t.timestamp)}</td>
              <td className="px-4 py-3 text-[#F5F5F0]">{t.metadata?.package_name || t.package_id}</td>
              <td className="px-4 py-3 text-[#F5F5F0]">${t.amount} {(t.currency || "").toUpperCase()}</td>
              <td className="px-4 py-3">
                <span className={`px-2 py-1 text-[10px] tracking-wider uppercase rounded-sm ${
                  t.payment_status === "paid"
                    ? "bg-emerald-900/40 text-emerald-300"
                    : "bg-[#0A0A0A] text-[#A3A3A3]"
                }`}>{t.payment_status}</span>
              </td>
              <td className="px-4 py-3 text-[#A3A3A3] font-mono text-xs">{t.session_id.slice(-12)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EventsTable({ events }) {
  if (events.length === 0) return <p className="mt-8 text-[#A3A3A3]">Még nincs esemény.</p>;
  return (
    <div className="mt-8 bg-[#111111] border border-[#2A2A2A] rounded-sm overflow-hidden">
      <table data-testid="admin-events-table" className="w-full text-sm">
        <thead className="bg-[#0A0A0A]">
          <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3]">
            <th className="px-4 py-3">Időpont</th>
            <th className="px-4 py-3">Esemény</th>
            <th className="px-4 py-3">Forrás</th>
            <th className="px-4 py-3">Nyelv</th>
            <th className="px-4 py-3">Útvonal</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#2A2A2A]">
          {events.map((e) => (
            <tr key={e.id}>
              <td className="px-4 py-3 text-[#A3A3A3] whitespace-nowrap">{formatDate(e.timestamp)}</td>
              <td className="px-4 py-3"><span className="inline-flex items-center gap-2 text-[#F5F5F0]"><MousePointerClick className="w-3.5 h-3.5 text-[#D4AF37]" /> {e.event}</span></td>
              <td className="px-4 py-3 text-[#A3A3A3]">{e.metadata?.source || "—"}</td>
              <td className="px-4 py-3 text-[#A3A3A3]">{e.language || "—"}</td>
              <td className="px-4 py-3 text-[#A3A3A3]">{e.path || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
